import {type FC, useCallback, useContext, useEffect, useMemo, useState} from "react";

import type {Listing} from "../../models/listing";
import type {UserRaw} from "../../models/user";
import {User} from "../../models/user";
import ListingService from "../../services/listing";
import UserService from "../../services/user";
import {filterVisibleListings} from "../../utils/listings";
import HTTPContext from "../contexts/HTTPContext";
import type {UserContextType} from "../contexts/UserContext";
import {defaultValue, UserContext} from "../contexts/UserContext";

const UserProvider: FC<{children: React.ReactNode}> = ({children}) => {
    const http = useContext(HTTPContext);
    const [value, setValue] = useState<UserContextType>(defaultValue);

    const updateUser = useCallback(async () => {
        const listingService = new ListingService(http);
        const userService = new UserService(http);

        try {
            const [user, seamToken] = await Promise.all([
                userService.fetchUser(),
                userService.fetchSeamClientToken()
            ]);

            const listings = await listingService.fetchAllListings(user.isDemo);
            const visibleListings = filterVisibleListings<Listing>(listings);

            setValue(prev => ({
                ...prev,
                isLoading: false,
                isAuthenticated: true,
                loggedInTime: new Date(),
                user,
                listings,
                visibleListings,
                // uncomment to test onboarding flow
                // listings: [],
                // visibleListings: [],
                listingGroups: user.listingGroups,
                locks: user.locks,
                permissions: user.permissions,
                seamToken,
                isDemo: user.isDemo
            }));
        } catch {
            setValue(prev => ({
                ...prev,
                isLoading: false
            }));
        }
    }, [http]);

    useEffect(() => {
        updateUser();
    }, [updateUser]);

    const setUser = useCallback((userProps: Partial<UserRaw>) => {
        setValue(prev => {
            const nextUser = new User({...prev.user.raw, ...userProps} as UserRaw);
            return {
                ...prev,
                user: nextUser,
                permissions: nextUser.permissions
            };
        });
    }, []);

    const logout = useCallback(async () => {
        await http.get("/logout");
        setValue(prev => ({
            ...prev,
            ...defaultValue,
            isLoading: false
        }));
    }, [http]);

    return (
        <UserContext.Provider
            value={useMemo(() => {
                return {
                    ...value,
                    setUser,
                    setState: setValue,
                    updateUser,
                    logout
                };
            }, [value, setUser, updateUser, logout])}
        >
            {children}
        </UserContext.Provider>
    );
};

export default UserProvider;
